import React from "react";
import produce from "immer";

import { Accordion, Grid, AccordionDetails, AccordionSummary } from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";

import { ReactComponent as DeleteSlotIcon } from "../../../assets/svg/DeleteIcon.svg";
import { generateDropdownItems } from "../../../../utils/helperFunctions";
import { ReactComponent as EditIcon } from "../../../assets/svg/Pencil.svg";

import styles from "./RangeGeneratorFormStyles";
import LinkButton from "../../../common/LinkButton/LinkButton";
import SelectBox from "../../../common/SelectBox/SelectBox";
import TimePicker from "../../../common/TimePicker/TimePicker";
import { TEXT_CONSTANT } from "../DefaultInterface";
import PriorityFeeField from "../../../common/PriorityFeeField/PriorityFeeField";
import { parseTimeValue } from "utils/DateUtils";

const MINIMUM_DAYS = generateDropdownItems(0, 10, "days");
const MAXIMUM_DAYS = generateDropdownItems(0, 20, "days");

interface EditSlotRangeProps {
  tabIndex: number;
  formIndex: number;
  dayWiseSlots: any;
  generatedSlots: any;
  onIndividualRangeFieldsChange: any;
  handleUpdateConfig: any;
  makeSlotEditable: any;
  cancelEditingASlot: any;
  addNewTemplateSlot: any;
  handleDeleteTemplateSlot: any;
}

const EditSlotRange = (props: EditSlotRangeProps) => {
  const { tabIndex, formIndex, dayWiseSlots, generatedSlots, handleUpdateConfig, onIndividualRangeFieldsChange, makeSlotEditable, cancelEditingASlot, addNewTemplateSlot, handleDeleteTemplateSlot } = props;
  const classes = styles();
  const reg = new RegExp("^[0-9]+$");

  const handleConfigChange = (event: any, dayName: string, slotIndex: number) => {
    const { name, value } = event.target;
    if (name === "slotCapacity" && value !== "" && !reg.test(value)) {
      return;
    }

    let newRangeConfig: any = produce(generatedSlots[dayName], (draft: any) => {
      draft[slotIndex][name] = value;
    });
    onIndividualRangeFieldsChange(tabIndex, { ...generatedSlots, [dayName]: newRangeConfig });
  };

  const cutOffTimeChange = (date: any, dayName: string, slotIndex: number) => {
    let newRangeConfig: any = produce(generatedSlots[dayName], (draft: any) => {
      draft[slotIndex].cutOff = date;
    });
    onIndividualRangeFieldsChange(tabIndex, { ...generatedSlots, [dayName]: newRangeConfig });
  };

  const renderEditSlotRange = (slots: any, index: number, day: string) => {
    return (
      <>
        <Grid container spacing={1}>
          <Grid item xs={2} className={classes.slotsTableLabel}>
            {`${TEXT_CONSTANT.MINIMUM_DAYS}`}
          </Grid>
          <Grid item xs={2} className={classes.slotsTableLabel}>
            {`${TEXT_CONSTANT.MAXIMUM_DAYS}`}
          </Grid>
          <Grid item xs={2} className={classes.slotsTableLabel}>
            {`${TEXT_CONSTANT.CUT_OFF}`}
          </Grid>
          <Grid item xs={2} className={classes.slotsTableLabel}>
            {`${TEXT_CONSTANT.PRIORITY_FEE}`}
          </Grid>
          <Grid item xs={2} className={classes.slotsTableLabel}>
            {`${TEXT_CONSTANT.ACTIONS}`}
          </Grid>
        </Grid>
        {slots.map((slotInfo: any, slotIndex: number) => {
          const { canBeUpdated, isUpdating }: any = dayWiseSlots?.[day]?.find((d: any) => d.id === slotInfo.id) || {};
          const canSlotBeUpdated = slotInfo?.isNewSlot || canBeUpdated;
          return (
            <Grid container spacing={1}>
              <Grid item xs={2}>
                <SelectBox name="minDay" menuItems={MINIMUM_DAYS} value={slotInfo.minDay} disabled={!canSlotBeUpdated} handleChange={(e: any) => handleConfigChange(e, slotInfo.day, slotIndex)} />
              </Grid>
              <Grid item xs={2}>
                <SelectBox name="maxDay" menuItems={MAXIMUM_DAYS} disabled={!canSlotBeUpdated} value={slotInfo.maxDay} handleChange={(e: any) => handleConfigChange(e, slotInfo.day, slotIndex)} />
              </Grid>
              <Grid item xs={2}>
                <TimePicker
                  value={parseTimeValue(slotInfo.cutOff)}
                  handleChange={(date: any) => cutOffTimeChange(date, slotInfo.day, slotIndex)}
                  disabled={!canSlotBeUpdated}
                  fieldLabel={undefined}
                  className={undefined}
                  timerProps={undefined}
                />
              </Grid>
              <Grid item xs={2}>
                <PriorityFeeField
                  value={slotInfo.priorityFee}
                  onChange={(e: any) => handleConfigChange(e, slotInfo.day, slotIndex)}
                  disabled={!canSlotBeUpdated}
                />
              </Grid>
              <Grid item xs={2} style={{ alignSelf: "center" }}>
                <Grid container spacing={4}>
                  {canSlotBeUpdated ? (
                    <Grid item xs={10}>
                      <Grid container spacing={4} alignItems="center">
                        <Grid item xs={4}>
                          <LinkButton
                            className={classes.updateSlotButton}
                            buttonLabel={isUpdating ? "saving..." : "Save"}
                            disabled={isUpdating || slotInfo.slotCapacity === ""}
                            onClick={() => handleUpdateConfig({ slotIndex, slot: slotInfo, generatorFormIndex: index })}
                          />
                        </Grid>
                        <Grid item xs={4}>
                          <LinkButton
                            className={classes.updateSlotButton}
                            buttonLabel="Cancel"
                            disabled={isUpdating}
                            onClick={() => {
                              cancelEditingASlot({ slotIndex, slot: slotInfo, generatorFormIndex: index });
                            }}
                          />
                        </Grid>
                        <Grid item xs={4}>
                          <DeleteSlotIcon className={classes.iconButton} onClick={() => handleDeleteTemplateSlot({ slotIndex, slot: slotInfo, generatorFormIndex: index })} />
                        </Grid>
                      </Grid>
                    </Grid>
                  ) : (
                    <Grid item xs={2}>
                      <EditIcon className={classes.iconButton} onClick={() => makeSlotEditable(slotInfo)} />
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>
          )
        })}

        <Grid item xs={12}>
          <LinkButton buttonLabel="Add New Range" onClick={() => addNewTemplateSlot(index, slots[0])} />
        </Grid>
      </>
    );
  };

  return (
    <div className={classes.slotsGeneratorForm}>
      {Object.keys(generatedSlots).map((element: any, index: number) => (
        <Accordion key={element} defaultExpanded={true} className={classes.whiteBackground}>
          <AccordionSummary className={classes.slotsGeneratorAccordionSummary} expandIcon={<ExpandMoreIcon />} classes={{ expanded: classes.expandedStyle }}>
            <Grid container>
              <Grid item xs={6} className={classes.daysInput}>
                <div>
                  {`Slots for : `}
                  <span className={classes.dayStyles}>{element.toLowerCase()}</span>
                </div>
              </Grid>
            </Grid>
          </AccordionSummary>
          <AccordionDetails className={classes.slotsGeneratorAccordionDetails} style={{ paddingBottom: 20 }}>
            {generatedSlots[element]?.length > 0 ? renderEditSlotRange(generatedSlots[element], index, element) : null}
          </AccordionDetails>
        </Accordion>
      ))}
    </div>
  );
};

export default EditSlotRange;
